import type { Logger } from '@engined/core/services/logger.js';
import * as Sentry from '@sentry/core';
import React from 'react';
import type { ErrorInfo } from 'react-dom/client';
import { hydrateRoot } from 'react-dom/client';

const BROWSER = {
  messenger: /\bFB[\w_]+\/(Messenger|MESSENGER)/,
  facebook: /\bFB[\w_]+\//,
  twitter: /\bTwitter/i,
  line: /\bLine\//i,
  wechat: /\bMicroMessenger\//i,
  puffin: /\bPuffin/i,
  miui: /\bMiuiBrowser\//i,
  instagram: /\bInstagram/i,
  chrome: /\bCrMo\b|CriOS|Android.*Chrome\/[.0-9]* (Mobile)?/,
  safari: /Version.*Mobile.*Safari|Safari.*Mobile|MobileSafari/,
  ie: /IEMobile|MSIEMobile/,
  firefox: /fennec|firefox.*maemo|(Mobile|Tablet).*Firefox|Firefox.*Mobile|FxiOS/,
};

function browser(): string {
  for (const [key, regex] of Object.entries(BROWSER)) {
    if (regex.test(this.ua)) {
      return key;
    }
  }

  return 'other';
}

function isInApp(): boolean {
  const rules = ['WebView', '(iPhone|iPod|iPad)(?!.*Safari/)', 'Android.*(wv)'];
  const regex = new RegExp(`(${rules.join('|')})`, 'ig');
  return Boolean(navigator.userAgent.match(regex));
}

export function fixReactVSBrowserExtensions() {
  // Fix browser extensions inserting code in <html></html>
  // https://github.com/facebook/react/issues/24430
  try {
    document
      .querySelectorAll(
        'html > *:not(body, head), script[src*="extension://"], link[href*="extension://"], script[src*="scr.kaspersky-labs.com"], link[href*="scr.kaspersky-labs.com"]',
      )
      .forEach((s) => {
        s.parentNode.removeChild(s);
      });
    for (const child of Array.from(document.head.childNodes)) {
      // Remove everything before our first tag
      if ((child as HTMLElement).dataset?.first === 'true') {
        break;
      }
      child.remove();
    }

    if (isInApp()) {
      document.querySelector('script#context-script').innerHTML = '';
    }
  } catch (err) {
    Sentry.captureException(err);
  }
}

let hydrationErrorsCount = 0;
export function logHydrationErrors(logger: Logger, error: unknown, errorInfo: ErrorInfo) {
  if (
    error &&
    ((error as Error).message ===
      'Hydration failed because the initial UI does not match what was rendered on the server.' ||
      (error as Error).message?.startsWith('Minified React error #418'))
  ) {
    if (hydrationErrorsCount === 0) {
      logger.error(error, errorInfo);
    }
    hydrationErrorsCount++;
  } else {
    logger.error(error, errorInfo);
  }
}

export function hydrate(App: React.ComponentType, logger: Logger, beforeRecovery?: () => void) {
  const hydrationError: { error: boolean; ssr: string; csr: string } = {
    error: false,
    ssr: document.firstElementChild.innerHTML,
    csr: null,
  };

  let recovering = false;
  window.addEventListener('error', (event) => {
    const msg = event.message.toLowerCase();
    const isHydrationMsg =
      msg.includes('hydration') ||
      msg.includes('hydrating') ||
      msg.match(/reactjs\.org\/docs\/error-decoder\.html\?invariant=(418|419|422|423|425)/) ||
      msg.match(/(does not match server-rendered HTML|Hydration failed because)/i);

    if (isHydrationMsg) {
      hydrationError.error = true;
      hydrationError.csr = document.firstElementChild.innerHTML;
      (async () => {
        if (hydrationError.ssr !== hydrationError.csr) {
          const prettier = await import('prettier/standalone');
          const html = await import('prettier/plugins/html');
          const ssr = await prettier.default.format(hydrationError.ssr, { parser: 'html', plugins: [html.default] });
          const csr = await prettier.default.format(hydrationError.csr, { parser: 'html', plugins: [html.default] });
          const diff = await import('diff');
          const patch = diff.default.createTwoFilesPatch('ssr.html', 'csr.html', ssr, csr);
          console.log(patch);
          Sentry.captureMessage('Hydration error', {
            extra: {
              patch,
            },
          });
        }
      })();

      if (!recovering) {
        recovering = true;
        setTimeout(() => {
          beforeRecovery?.();
          root.render(React.createElement(App));
        }, 0);
      }
    }
  });

  fixReactVSBrowserExtensions();

  const root = hydrateRoot(document, React.createElement(App), {
    onRecoverableError: (error, errorInfo) => {
      logHydrationErrors(logger, error, errorInfo);

      if (!recovering) {
        recovering = true;
        setTimeout(() => {
          beforeRecovery?.();
          root.render(React.createElement(App));
        }, 0);
      }
    },
  });
}
